import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Personas } from '../services/acl.service';
import { AclService } from '../services/acl.service';
import { institutionHasSites } from '../utilities/sites.utils';
import { Institution, User } from '../models/user';

export interface PersonaEntry {
  _id: string;
  value: string;
}

@Injectable({
  providedIn: 'root',
})
export class PersonasService {
  private generalPersonas: PersonaEntry[] = [
    {
      _id: Personas.InstitutionAdmin,
      value: this.translateService.instant('Institution Admin'),
    },
    {
      _id: Personas.SiteAdmin,
      value: this.translateService.instant('Site Admin'),
    },
    {
      _id: Personas.Teacher,
      value: this.translateService.instant('Teacher'),
    },
    {
      _id: Personas.TeachingAssistant,
      value: this.translateService.instant('Teaching Assistant'),
    },
    {
      _id: Personas.Student,
      value: this.translateService.instant('Student'),
    },
    {
      _id: Personas.Parent,
      value: this.translateService.instant('Parent'),
    },
  ];

  constructor(private aclService: AclService, private translateService: TranslateService) {}

  get allGeneralPersonas(): PersonaEntry[] {
    return this.generalPersonas;
  }

  /**
   * Returns a list of institution PersonaEntry objects. It will filter out the SiteAdmin persona if the institution does not have any sites
   */
  getInstitutionPersonasList(institution?: Institution): PersonaEntry[] {
    if (institutionHasSites(institution)) {
      return this.generalPersonas;
    }

    return this.generalPersonas.filter((persona) => persona._id !== Personas.SiteAdmin);
  }

  /**
   * Returns a map of institution PersonaEntry objects with the id as the key.
   */
  getInstitutionPersonasMap(institution?: Institution): Map<string, PersonaEntry> {
    return new Map(
      this.getInstitutionPersonasList(institution).map((persona) => [persona._id, persona]),
    );
  }

  /**
   * Returns a list of PersonaEntry options allowed for a given user to choose from.
   */
  getUserAllowedPersonaOptions(user: User): PersonaEntry[] {
    const personas = this.getInstitutionPersonasList(user.institution);

    if (this.aclService.isSiteAdmin(user)) {
      return personas.filter((persona) => persona._id !== Personas.InstitutionAdmin);
    }

    return personas;
  }

  /**
   * Resolves a user's first persona to a display label. Preference order:
   * the institution's custom persona name, then the translated default label
   * (e.g. "InstitutionAdmin" -> "Institution Admin"), then the raw persona key
   * as a last resort.
   */
  getPersonaDisplayName(personas: string[] | undefined, institution?: Institution): string {
    const personaKey = personas?.[0];
    if (!personaKey) {
      return '';
    }

    return (
      institution?.personas?.[personaKey]?.name ||
      this.getInstitutionPersonasMap(institution).get(personaKey)?.value ||
      personaKey
    );
  }
}
